import { Box, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import React, { forwardRef, Fragment, useImperativeHandle, useMemo, useRef } from 'react';

import { distributePaste, MAX_OCTET_DIGITS, OCTET_COUNT, toOctets, toValue } from './ipv4Segments';

/**
 * Segmented IPv4 address entry — four octet boxes with the dots drawn between
 * them rather than typed. Used wherever an operator points equipment at a
 * server address, where a mistyped octet is a field visit to recover.
 *
 * The separators are chrome, not content: a pasted "10.20.30.40" spreads across
 * the boxes and its dots are consumed, and typing "." simply advances to the
 * next octet. The value handed to the caller is always the joined dotted quad,
 * so consumers see a plain string and never the segmentation.
 *
 * The segments live inside a single house-skin TextField rather than beside a
 * standalone label, so the control sits at the same height, background and
 * label position as every other field on a form.
 *
 * Contains:
 * - Ipv4Field — the controlled field (default export)
 */

const segmentSx = {
  width: '3ch',
  border: 0,
  padding: 0,
  margin: 0,
  background: 'transparent',
  color: 'inherit',
  font: 'inherit',
  textAlign: 'center',
  outline: 'none',
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { display: 'none' },
};

/**
 * The interior of the filled input: four bare segments and the dots between.
 *
 * Rendered through MUI's `inputComponent` slot, so it inherits the theme's
 * filled paddings via the `className` MUI hands down and needs no skin of its
 * own.
 */
const Ipv4SegmentedInput = forwardRef(function Ipv4SegmentedInput(props, ref) {
  const { octets, onSegmentsChange, label, className, disabled, id } = props;
  const inputRefs = useRef([]);

  // MUI focuses the input slot when the label or wrapper is clicked; send that
  // to the first segment so the control behaves like one field.
  useImperativeHandle(ref, () => ({
    focus: () => inputRefs.current[0]?.focus(),
  }));

  const focusSegment = index => {
    const target = inputRefs.current[index];
    if (target) {
      target.focus();
      target.select();
    }
  };

  const commit = (index, digits) => {
    onSegmentsChange(octets.map((octet, i) => (i === index ? digits : octet)));
  };

  const handleChange = index => event => {
    const digits = event.target.value.replace(/\D/g, '').slice(0, MAX_OCTET_DIGITS);
    commit(index, digits);
    if (digits.length === MAX_OCTET_DIGITS && index < OCTET_COUNT - 1) {
      focusSegment(index + 1);
    }
  };

  const handleKeyDown = index => event => {
    const { selectionStart, selectionEnd, value: current } = event.target;
    if (event.key === '.') {
      event.preventDefault();
      if (index < OCTET_COUNT - 1) focusSegment(index + 1);
      return;
    }
    if (event.key === 'Backspace' && current === '' && index > 0) {
      event.preventDefault();
      focusSegment(index - 1);
      return;
    }
    if (event.key === 'ArrowLeft' && selectionStart === 0 && selectionEnd === 0 && index > 0) {
      event.preventDefault();
      focusSegment(index - 1);
      return;
    }
    if (event.key === 'ArrowRight' && selectionStart === current.length && index < OCTET_COUNT - 1) {
      event.preventDefault();
      focusSegment(index + 1);
    }
  };

  const handlePaste = index => event => {
    const pasted = event.clipboardData.getData('text');
    if (!pasted) return;
    event.preventDefault();
    const { octets: next, focusIndex } = distributePaste(octets, index, pasted);
    onSegmentsChange(next);
    window.requestAnimationFrame(() => focusSegment(focusIndex));
  };

  return (
    <Box className={className} sx={{ display: 'flex', alignItems: 'center' }}>
      {octets.map((octet, index) => (
        <Fragment key={index}>
          {index > 0 ? (
            <Box component="span" aria-hidden sx={{ userSelect: 'none', opacity: 0.6, px: 0.75 }}>
              .
            </Box>
          ) : null}
          <Box
            component="input"
            id={index === 0 ? id : undefined}
            sx={segmentSx}
            value={octet}
            disabled={disabled}
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            maxLength={MAX_OCTET_DIGITS}
            aria-label={`${label} octet ${index + 1}`}
            onChange={handleChange(index)}
            onKeyDown={handleKeyDown(index)}
            onPaste={handlePaste(index)}
            ref={element => {
              inputRefs.current[index] = element;
            }}
          />
        </Fragment>
      ))}
    </Box>
  );
});

Ipv4SegmentedInput.propTypes = {
  octets: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSegmentsChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
};

/**
 * @param {Object}   props
 * @param {string}   props.value        - Dotted quad, or '' when empty.
 * @param {function} props.onChange     - Called with the joined dotted quad.
 * @param {string}   props.label        - Field label.
 * @param {boolean}  [props.error]      - Render the error state.
 * @param {string}   [props.helperText] - Helper or error text beneath the field.
 * @param {boolean}  [props.disabled]   - Disable every segment.
 * @param {string}   [props.name]       - Field name.
 * @returns {JSX.Element} The segmented address field.
 */
const Ipv4Field = ({ value, onChange, label, error = false, helperText = '', disabled = false, name }) => {
  const octets = useMemo(() => toOctets(value), [value]);

  return (
    <TextField
      label={label}
      name={name}
      error={error}
      helperText={helperText}
      disabled={disabled}
      fullWidth
      // The segments are always visible, so the label can never sit over them.
      InputLabelProps={{ shrink: true }}
      InputProps={{
        inputComponent: Ipv4SegmentedInput,
        inputProps: {
          octets,
          label,
          disabled,
          onSegmentsChange: next => onChange(toValue(next)),
        },
      }}
    />
  );
};

Ipv4Field.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  name: PropTypes.string,
};

export default Ipv4Field;
